import React, { useState, Component } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { MyceliumNetworkHero } from '../components/MyceliumNetworkHero';
import { DNAHelixHero } from '../components/DNAHelixHero';
interface TeamMember {
  name: string;
  role: string;
  story: string;
  background: string;
  image: string;
}
interface AdvisorMember {
  name: string;
  role: string;
  bio: string;
  image?: string;
}
interface CommitteeMember {
  name: string;
  bio: string;
  image?: string;
}
export function TeamSplitScreen() {
  const [selectedMember, setSelectedMember] = useState<{
    type: 'core' | 'advisor' | 'committee';
    index: number;
  } | null>(null);
  const {
    scrollYProgress
  } = useScroll();
  const coreTeam: TeamMember[] = [{
    name: 'Natalya Wallin',
    role: 'Founder & Partnership Builder',
    story: "Natalya is a systems thinker who has spent her career at the intersection of science, policy, social impact, and global capital. Her background spans biology, public policy, and business which gives her a unique lens with which to see the evolving biomimicry opportunity and build something new around it. Before founding Vitality, she helped build and launch a global fund to address forced labor risks in supply chains. She served as managing director, overseeing deployment of $75 million in public and private funding across nine countries. Her previous experience engaging the White House, State Department, corporations, and civil society organizations informs the cross-sector fluency that now drives Vitality's thesis and partnerships.",
    background: 'She is also an ultrarunner and 8th place female finisher at Moab 240 which signals how she operates: long-horizon, high-tolerance for the unknown, built for endurance. Natalya holds degrees in Biology and Public Policy, as well as an Executive MBA from Duke University.',
    image: 'https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg'
  }, {
    name: 'Patrick Livingston',
    role: 'Built Environment Expert & Portfolio Construction',
    story: 'Patrick is an engineer and infrastructure builder who has spent 12 years bringing complex, large-scale projects from concept to completion. With dual degrees in Civil and Biological Engineering and an MBA from Duke, he sits at exactly the intersection this work requires: He has a deep understanding of how things are physically built and what it takes to finance them. He has delivered over $1 billion in constructed infrastructure, navigating the technical, regulatory, and financial layers that stop most projects before they start.',
    background: 'Patrick brings to Vitality what is often missing in early-stage investment: A rigorous, grounded understanding of what it actually takes to build in the physical world and an understanding of what patient, well-structured capital makes possible.',
    image: 'https://cdn.magicpatterns.com/uploads/oagLnQyesCH4gCMgV853L2/Pat_Headshot.jpg'
  }, {
    name: 'Rollin Nash',
    role: 'Operations & Resource Planning Specialist',
    story: 'Rollin is a builder who converts big strategic visions to tactics and implementation. A military veteran and systems architect from an Ohio farming community, he has spent 12 years planning and implementing high-stakes, multi-million dollar initiatives including first-in-human clinical trials, proof-of-concept military operations around the world, and complex organizational transformations. He knows exactly what it takes to build rigorous processes under real world pressures and constraints in local environments and global contexts. He brings that operational discipline to everything Vitality does.',
    background: 'His roots in a farming community and military experience give him a grounding in the physical and operational realities of delivering and documenting processes and results. Rollin earned his Bachelor’s from West Point while studying Kinesiology and Mechanical Engineering and earned his MBA from Duke.',
    image: 'https://cdn.magicpatterns.com/uploads/aQWr6mTKqjSUTCytDF8GyV/Headshot.jpg'
  }];
  const advisors: AdvisorMember[] = [{
    name: 'Melissa Kim',
    role: 'Advisor & Original Co-founder',
    bio: 'Experienced executive, entrepreneur, and Vitality co-founder who advises startups on driving product innovation, growth, and peak performance.',
    image: 'https://cdn.magicpatterns.com/uploads/7TaUSrmKqXQKcWYT8kcifC/Melissa_Headshot.jpg'
  }, {
    name: 'David Balkcom',
    role: 'Advisor, Finance & Agriculture',
    bio: 'Managing Director at FTI advising startups on turnaround, transformation, and due diligence, with deep experience in controlled environment agriculture and natural resources.',
    image: 'https://cdn.magicpatterns.com/uploads/8EqyD625fqmGoK7SfdyZho/David_Headshot.jpg'
  }, {
    name: 'Dr. Jean Baderschneider',
    role: 'Advisor, Global Engagement & Climate Finance',
    bio: 'Global supply chain and climate finance leader with 35 years of international experience, former VP of Global Procurement at ExxonMobil and non-executive Director of Fortescue Metals Group.',
    image: 'https://cdn.magicpatterns.com/uploads/oZoevN8jFFQA4NHrwEzrwg/Jean_Bio_pic_2026.png'
  }, {
    name: 'Matt Esposito',
    role: 'Advisor, Investor Relations',
    bio: 'Strategic advisor with deep expertise in venture capital and startup ecosystems.',
    image: 'https://cdn.magicpatterns.com/uploads/hYDVwGv8avLAL5fqGmTYmq/Matt_Headshot.jpg'
  }];
  const committee: CommitteeMember[] = [{
    name: 'Jordan McAlister',
    bio: 'Head of Investment & Innovation Strategy with Hyperspace Ventures',
    image: 'https://cdn.magicpatterns.com/uploads/sUj2TqUKbbrXFrhPY4ndmT/Jordan_Headshot.jpg'
  }, {
    name: 'Kate Knight',
    bio: 'Executive scaling coach and advisor with 20 years of experience',
    image: 'https://cdn.magicpatterns.com/uploads/ua9ycBvhPbjjrzg8hWCre9/Kate_Headshot.jpg'
  }, {
    name: 'Robin Berry',
    bio: 'Director of Lab to Market Innovation at ASU Biomimicry Center',
    image: 'https://cdn.magicpatterns.com/uploads/srnieMTZyCgwgg24unbedV/Robin_Headshot.jpg'
  }, {
    name: 'Olivia Hanrahan-Soar',
    bio: 'Expert in innovative finance and impact investing',
    image: 'https://cdn.magicpatterns.com/uploads/3V4HKj9gszSL8YVnqQCohF/8_Olivia.jpg'
  }, {
    name: 'Medina Korda Poole',
    bio: 'High-impact leader managing $150M+ portfolios globally',
    image: 'https://cdn.magicpatterns.com/uploads/ir1uoqUiVwJ7RcLbkkq7CA/6_Medina.png'
  }, {
    name: 'Chris Burton',
    bio: 'Experienced investor and operator with a focus on sustainable technologies.',
    image: 'https://cdn.magicpatterns.com/uploads/pRjZWzihcQPZV74t433sRU/5_Chris.jpg'
  }];
  return <div className="min-h-screen bg-black" data-id="element-2134" data-mp="17h0td6f35o9x">
      {/* Progress Indicator */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-vitalityTeal z-50 origin-left" style={{
      scaleX: scrollYProgress
    }} data-id="element-2135" data-mp="1mfcoh8mt8oyz" />

      {/* Hero Intro */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden" data-id="element-2136" data-mp="tpm9kxdctj1t">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] to-black" data-id="element-2137" data-mp="os3wqvw7ujoj" />

        {/* Background image - mycelium network microscope */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-15" data-id="element-2138" data-mp="13ibhxjdlygsx" />

        {/* Emphasized SVG overlay */}
        <div className="absolute inset-0 opacity-60" data-id="element-2139" data-mp="axl2x8uc8ohu">
          <MyceliumNetworkHero data-id="element-2140" data-mp="601oa7tqa9pd" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-2141" data-mp="5z09bs9ld32d">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-6xl md:text-8xl font-bold text-white mb-6" data-id="element-2142" data-mp="3go7dl2b3q4g8">
            Our Team
          </motion.h1>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="flex justify-center" data-id="element-2143" data-mp="3fched8qopej">
            <ChevronDown className="w-8 h-8 text-vitalityTeal animate-bounce" data-id="element-2144" data-mp="i50ibdd0dm1q" />
          </motion.div>
        </div>
      </section>

      {/* Core Team - Full Viewport Sections */}
      {coreTeam.map((member, index) => <SplitScreenSection key={member.name} member={member} index={index} imageOnLeft={index % 2 === 0} onReadMore={() => setSelectedMember({
      type: 'core',
      index
    })} data-id="element-2145" data-mp="r701ab7l307b" data-mpi={index} />)}

      {/* Advisors - Compact Section */}
      <section className="min-h-screen flex items-center bg-gradient-to-b from-black to-[#0e2533] py-20" data-id="element-2146" data-mp="22pf3ficue112">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-2147" data-mp="1xqkm6kuiwkj2">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-16" data-id="element-2148" data-mp="2udz28gw9mfgk">
            <h2 className="text-5xl font-bold text-white mb-4" data-id="element-2149" data-mp="2gmkexxvbrs3p">Advisors</h2>
            <p className="text-gray-400 text-xl" data-id="element-2150" data-mp="3e3j5lbtl2vh7">
              Strategic guidance from industry veterans
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-2151" data-mp="399wtbkrc0tlx">
            {advisors.map((advisor, index) => <motion.div key={advisor.name} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2152" data-mp="3b3ttngx7wxsi" data-mpi={index}>
                <div className="flex items-start gap-4 sm:gap-6" data-id="element-2153" data-mp="7k8p66lsezee">
                  {advisor.image ? <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex-shrink-0 overflow-hidden border-2 border-[#a0e6e9]/30" data-id="element-2154" data-mp="2n8m84k8icri">
                      <img src={advisor.image} alt={`${advisor.name} headshot`} className="w-full h-full object-cover" data-id="element-2155" data-mp="gjvuv4ljurd9" />
                    </div> : <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#a0e6e9]/20 to-[#a0e6e9]/5 flex items-center justify-center flex-shrink-0 border-2 border-[#a0e6e9]/30" data-id="element-2156" data-mp="106fhcywgh9zf">
                      <span className="text-2xl sm:text-3xl font-bold text-[#a0e6e9]/70" data-id="element-2157" data-mp="q89eadywxxsz">
                        {advisor.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-2158" data-mp="hah05d4xgw76">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" data-id="element-2159" data-mp="haz1go6g60ly">
                      {advisor.name}
                    </h3>
                    <p className="text-[#a0e6e9] text-sm font-medium mb-3" data-id="element-2160" data-mp="7ibbac9z2uw7">
                      {advisor.role}
                    </p>
                    <p className="text-gray-400 text-sm sm:text-base" data-id="element-2161" data-mp="3pphhbc2mqkg4">
                      {advisor.bio}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Committee - Simple List */}
      <section className="py-20 bg-[#0e2533]" data-id="element-2162" data-mp="1vfj2159mup6m">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2163" data-mp="1qh96gtjzxmkh">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12" data-id="element-2164" data-mp="1qfodh8dbyboq">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2165" data-mp="2e5imtfyfxi48">
              Investment Review Committee
            </h2>
            <p className="text-gray-500 text-lg mb-2" data-id="element-2166" data-mp="256x6lrgfyf6m">
              Expert evaluation and due diligence
            </p>
            <p className="text-gray-400 text-sm italic" data-id="element-2167" data-mp="yquwh8f3sue3">
              These experts contribute their insights on a voluntary basis in
              support of the mission
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2168" data-mp="35fwpthjjqtlz">
            {committee.map((member, index) => <motion.div key={member.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-6 rounded-xl bg-white/5 border border-white/10" data-id="element-2169" data-mp="3ae6i9kdvzg3w" data-mpi={index}>
                <div className="flex items-start gap-4" data-id="element-2170" data-mp="1waf8vhm2tmv">
                  {member.image ? <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden border-2 border-white/10" data-id="element-2171" data-mp="5xdtjdk0ghlz">
                      <img src={member.image} alt={`${member.name} headshot`} className="w-full h-full object-cover" data-id="element-2172" data-mp="27r387shl1tk0" />
                    </div> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border-2 border-white/10" data-id="element-2173" data-mp="18f1rwrt8c081">
                      <span className="text-lg font-bold text-white/50" data-id="element-2174" data-mp="1hhb0vd8unljb">
                        {member.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-2175" data-mp="os3u5qz8rpmz">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-2176" data-mp="osx38yloar8a">
                      {member.name}
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-2177" data-mp="13i1nelc135j1">{member.bio}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence data-id="element-2178" data-mp="614b16efha1m">
        {selectedMember !== null && <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedMember(null)} data-id="element-2179" data-mp="5yqe0s06tnrf">
            <motion.div className="bg-[#1c3f4d] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto border-2 border-vitalityTeal/30 relative" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-id="element-2180" data-mp="2cpcyegrqknsf">
              <button onClick={() => setSelectedMember(null)} className="absolute top-6 right-6 text-white hover:text-vitalityTeal transition-colors" data-id="element-2181" data-mp="1g0x4y9li9ua1">
                <X className="w-6 h-6" data-id="element-2182" data-mp="3mp7e041sxi6y" />
              </button>
              <div className="p-10" data-id="element-2183" data-mp="3hpb6vvxt5an6">
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-2" data-id="element-2184" data-mp="3hqcj43qk836h">
                  {coreTeam[selectedMember.index].role}
                </p>
                <h3 className="text-4xl font-bold text-white mb-8" data-id="element-2185" data-mp="38v6j5ldz0fbv">
                  {coreTeam[selectedMember.index].name}
                </h3>
                <div className="space-y-6" data-id="element-2186" data-mp="2yxs51sfis2mq">
                  <div data-id="element-2187" data-mp="2ywqpbank6ryo">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2188" data-mp="2yzlf7hpcd4zh">Story</h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2189" data-mp="2tyxlupdn4c23">
                      {coreTeam[selectedMember.index].story}
                    </p>
                  </div>
                  <div data-id="element-2190" data-mp="s610a8m3tr3q">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2191" data-mp="s69ga9dixcj5">
                      Background
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2192" data-mp="2633qct5dih8j">
                      {coreTeam[selectedMember.index].background}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Split Screen Section Component
function SplitScreenSection({
  member,
  index,
  imageOnLeft,
  onReadMore
}: {
  member: TeamMember;
  index: number;
  imageOnLeft: boolean;
  onReadMore: () => void;
}) {
  const {
    scrollYProgress
  } = useScroll();
  const imageY = useTransform(scrollYProgress, [index * 0.25, (index + 1) * 0.25], ['20%', '-20%']);
  return <section className="min-h-screen flex items-center relative overflow-hidden bg-black" data-id="element-2193" data-mp="213zic5u6qpjs">
      <div className="w-full" data-id="element-2194" data-mp="211mu5axu3iya">
        <div className="grid lg:grid-cols-2 lg:min-h-screen" data-id="element-2195" data-mp="37kl3rmqo40hd">
          {/* Image Side */}
          <motion.div className={`relative min-h-[50vh] lg:min-h-0 ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? -50 : 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2196" data-mp="3cimu8sqfb22v">
            <motion.div className="absolute inset-0" style={{
            y: imageY
          }} data-id="element-2197" data-mp="2s6cvj7m4719r">
              <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-black/50 to-transparent z-10" data-id="element-2198" data-mp="31wxr0m6hrpog" />
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" style={{
              objectPosition: 'center 20%'
            }} data-id="element-2199" data-mp="3fx8jturd3ec9" />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div className={`flex items-center px-6 sm:px-8 md:px-16 py-12 lg:py-20 ${imageOnLeft ? 'lg:order-2' : 'lg:order-1'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? 50 : -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-2200" data-mp="1hkiapq62lpwr">
            <div data-id="element-2201" data-mp="spb1vlf9j3o9">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4
            }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-2202" data-mp="spueqnq231vf">
                {member.role}
              </motion.p>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="text-5xl md:text-6xl font-bold text-white mb-8" data-id="element-2203" data-mp="3hkqw6lg0g0xy">
                {member.name}
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.6
            }} className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8" data-id="element-2204" data-mp="3rcwx4ko8pspa">
                {member.story}
              </motion.p>

              <motion.button initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.7
            }} onClick={onReadMore} className="inline-flex items-center gap-2 px-6 py-3 min-h-[44px] bg-vitalityTeal/10 hover:bg-vitalityTeal/20 border border-vitalityTeal/30 rounded-lg text-vitalityTeal font-semibold transition-colors" data-id="element-2205" data-mp="27fo65c73yzpb">
                Read Full Background
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}